import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { AppUpdateService } from './shared/services/app-update/app-update.service';
import { TenantBrandingService } from './shared/services/tenant-branding/tenant-branding.service';
import { ToastService } from './shared/services/toast/toast.service';
import { StorageService } from './storage.service';
import { GroupsService } from './views/groups/groups.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'skillztank';
  userIsLoggedIn = false;

  get createWorkspaceUrl(): string {
    const base = (environment.apiUrl || '').replace(/\/+$/, '');
    const cancelUrl = encodeURIComponent('https://skillztank.com');
    return `${base}/billing/onboarding?cancel_url=${cancelUrl}`;
  }

  constructor(
    private authService: AuthService,
    private groupService: GroupsService,
    public translate: TranslateService,
    private aroute: ActivatedRoute,
    private toast: ToastService,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private appUpdateService: AppUpdateService,
    public tenantBrandingService: TenantBrandingService
  ) {
    this.authService.getUserConfigurationDocument();
    translate.addLangs(['en', 'fr']);
    translate.setDefaultLang('fr');
    const storedLang = localStorage.getItem('language');
    if (storedLang === 'fr' || storedLang === 'en') {
      this.translate.use(storedLang);
    } else {
      if (navigator.language.includes('en')) {
        this.translate.use('en');
        localStorage.setItem('language', 'en');
      } else {
        this.translate.use('fr');
        localStorage.setItem('language', 'fr');
      }
    }

    this.aroute.queryParamMap.subscribe((params) => {
      const token = params.get('login');
      if (token && token !== 'null') {
        setTimeout(() => {
          this.authService.loginWithToken(token);
        }, 1000);
      }
    });
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'snackbar' && event.newValue) {
        switch (event.newValue) {
          case 'copied-content':
            this.toast.snackbar.open(this.translate.instant('Snackbar.CopiedNotification'), 'Ok', { duration: 2000 });
            localStorage.removeItem('snackbar');
            break;
          default:
            break;
        }
      }
    });
  }

  ngOnInit(): void {
    document.body.classList.add('app-loaded');
    if (!localStorage.getItem('loginData')) {
      this.authService.getValidToken();
    }
    const uid = this.authService.userInfo()?._id;
    if (uid && uid !== 'none') {
      this.authService.startSessionSubscription(uid);
      this.groupService.getUserGroups().subscribe();
    }
    this.setupBackgroundSyncListener();

    // Start version polling on deployed environments (not localhost)
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalhost) {
      this.appUpdateService.startPolling();
    }
  }

  private setupBackgroundSyncListener(): void {
    const getBackgroundChunksAndUpdate = () => {
      this.getAllBackgroundChunks()
        .then(async (chunks) => {
          for (const chunk of chunks) {
            const contentID = chunk.contentID;
            delete chunk.contentID;
            await this.storageService.store('chunks', chunk);
            const content = (await this.storageService.find('contents', [contentID]))[0];
            const ifPresentAlready = content?.contents?.find((element: any) => element._id === chunk._id);
            if (!ifPresentAlready) {
              content.contents.push({ _id: chunk._id, __v: 1 });
              await this.storageService.update('contents', contentID, {
                contents: content.contents,
              });
            }
          }
          if (chunks && chunks.length > 0) {
            setTimeout(() => {
              this.notificationService.sendNotification(chunks);
            }, 1000);
          }
          this.deleteAllBackgroundDocuments('chunks');
          this.deleteAllBackgroundDocuments('contents');
        })
        .catch((error) => console.error('Error processing background chunks:', error));
    };

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const message = event.data;
        if (message?.data?.type) {
          getBackgroundChunksAndUpdate();
        }
      });
    }
    getBackgroundChunksAndUpdate();
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('offline-chunks-db', 1);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('contents')) {
          db.createObjectStore('contents', { keyPath: '_id' });
        }
        if (!db.objectStoreNames.contains('chunks')) {
          db.createObjectStore('chunks', { keyPath: '_id' });
        }
      };

      request.onsuccess = (event) => {
        resolve((event.target as any).result);
      };

      request.onerror = (event) => {
        console.error('Database error:', (event.target as any).error);
        reject((event.target as any).error);
      };
    });
  }

  private getAllBackgroundChunks(): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      this.openDatabase()
        .then((db: IDBDatabase) => {
          if (!db.objectStoreNames.contains('chunks')) {
            resolve([]);
            return;
          }
          const transaction = db.transaction('chunks', 'readonly');
          const objectStore = transaction.objectStore('chunks');
          const request = objectStore.getAll();

          request.onsuccess = (event) => {
            resolve((event.target as any).result || []);
          };
          request.onerror = (event) => {
            console.error('Error getting all background chunks:', (event.target as any).error);
            reject((event.target as any).error);
          };
        })
        .catch((error) => {
          console.error('Error opening database for getAllBackgroundChunks:', error);
          reject(error);
        });
    });
  }

  private deleteAllBackgroundDocuments(collection: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.openDatabase()
        .then((db: IDBDatabase) => {
          if (!db.objectStoreNames.contains(collection)) {
            resolve(`Object store ${collection} does not exist.`);
            return;
          }
          const transaction = db.transaction(collection, 'readwrite');
          const objectStore = transaction.objectStore(collection);
          const request = objectStore.clear();

          request.onsuccess = () => {
            resolve(`All records deleted successfully from ${collection}.`);
          };
          request.onerror = (event) => {
            console.error(`Error deleting documents from ${collection}:`, (event.target as any).error);
            reject((event.target as any).error);
          };
        })
        .catch((error) => {
          console.error(`Error opening database for deleteAllBackgroundDocuments (${collection}):`, error);
          reject(error);
        });
    });
  }
}
